import { useActivityWorkflowActions } from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import { useCallback, useEffect, useState } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import {
  useCurrentActivity,
  useCurrentAsset,
  useNeedToRefresh,
} from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import { ActivityEditProps } from "./screens"
import ActivityEditHome from "./ActivityEditHome"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import ActivityScreenWrapper from "./ActivityScreenWrapper"
import ActivityLoading from "./ActivityLoading"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

interface ActivityEditManageProps extends ActivityEditProps {
  activityId?: string
  readonly?: boolean
  allowDelete?: boolean
  assetId?: string
}

const ActivityEditManage = ({
  activityId,
  onScreenChange,
  onBack,
  onChange,
  onClose,
  readonly = false,
  allowDelete,
  assetId,
}: ActivityEditManageProps) => {
  const activity = useCurrentActivity()
  const asset = useCurrentAsset()

  const { setActivity, setActivities, setAsset, hasRefreshed } = useActivityWorkflowActions()
  const needToRefresh = useNeedToRefresh()
  const [loading, setLoading] = useState(false)

  const [getActivity, { error: activityError }] = useLazyQueryWithEndpoint(
    gqlTypes.GetActivityDocument,
    {},
    "primary"
  )
  const [getActivities, { error: activitiesError }] = useLazyQueryWithEndpoint(
    gqlTypes.GetActivitiesDocument,
    {},
    "primary"
  )
  const [getAsset, { error: assetError }] = useLazyQueryWithEndpoint(
    gqlTypes.GetAssetDocument,
    {},
    "primary"
  )
  const error = activityError || activitiesError || assetError

  const fetchActivity = useCallback(
    async (activityId: string) => {
      if (!activityId) return
      const linkedActivityIds: string[] = []
      await getActivity({ variables: { activity: activityId } }).then(({ data }) => {
        const activity = data?.activity as gqlTypes.Activity | undefined
        if (activity?.source_links) {
          linkedActivityIds.push(...activity.source_links.map((link) => link.activity_id))
        }
        if (activity?.target_links) {
          linkedActivityIds.push(...activity.target_links.map((link) => link.activity_id))
        }
        if (activity) setActivity(activity)
        if (linkedActivityIds.length === 0 && activity) setActivities([activity])
      })
      if (linkedActivityIds.length > 0) {
        await getActivities({ variables: { activity: linkedActivityIds.join(",") } }).then(({ data }) => {
          const activities = data?.activities?.data as gqlTypes.Activity[] | undefined
          if (activities) setActivities(activities)
        })
      }
    },
    [getActivity, getActivities, setActivity, setActivities]
  )

  useEffect(() => {
    if (needToRefresh && activityId) {
      const showLoading = needToRefresh === "silent" ? false : true
      if (showLoading) setLoading(true)
      fetchActivity(activityId).finally(() => {
        if (showLoading) setLoading(false)
      })
      hasRefreshed()
    }
  }, [needToRefresh])

  useEffect(() => {
    if (activityId && activityId !== activity?.id) {
      setLoading(true)
      const loadActivity = async (activityId: string) => {
        if (!activityId) return
        await fetchActivity(activityId)
        if (assetId && assetId !== asset?.id) {
          await getAsset({ variables: { id: assetId } }).then(({ data }) => {
            const asset = data?.asset
            if (asset) setAsset(asset)
          })
        }
        setLoading(false)
      }
      loadActivity(activityId)
    }
  }, [activityId, assetId])

  console.log(loading || !activity || (!assetId && !asset))

  return loading ? (
    <ActivityLoading />
  ) : error ? (
    <ErrorAlert message={error.message} />
  ) : (
    <ActivityScreenWrapper>
      <ActivityEditHome
        readonly={readonly}
        allowDelete={allowDelete}
        onBack={onBack}
        onScreenChange={onScreenChange}
        onChange={onChange}
        onClose={onClose}
      />
    </ActivityScreenWrapper>
  )
}

export default ActivityEditManage
